import { useState } from "react";
import { FilePdf, CheckCircle, Circle } from "@phosphor-icons/react";
import { useApi, todayNZ, num, nzDate } from "@/lib/api";
import { Section, Btn, Field, Pill, useLoad, Empty } from "@/components/app/common";
import { downloadFile } from "@/pages/Inspections";

const minusDays = (d, n) => { const x = new Date(`${d}T00:00:00Z`); x.setUTCDate(x.getUTCDate() - n); return x.toISOString().slice(0, 10); };

export function Rotation({ farm, tick }) {
  const api = useApi();
  const [rows] = useLoad(() => api.get(`/feeding/rotation?farm_id=${farm.id}`), [farm.id, tick]);
  if (!rows) return null;
  const tone = (d) => (d == null ? "text-slate-400" : d >= 21 ? "text-green-700" : d >= 10 ? "text-amber-700" : "text-slate-900");
  return (
    <Section title="Days since last grazed" testid="paddock-rotation">
      {rows.length === 0 ? <Empty>Add paddocks in Settings to see the rotation.</Empty> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {rows.map((p) => (
            <div key={p.paddock} className="border border-slate-200 rounded-lg p-3" data-testid={`rotation-${p.paddock}`}>
              <p className="font-semibold text-sm">{p.paddock}{p.area_ha ? <span className="text-xs text-slate-500 font-normal"> · {p.area_ha} ha</span> : null}</p>
              <p className={`text-2xl font-bold font-mono-num ${tone(p.days_since)}`} data-testid={`rotation-${p.paddock}-days`}>{p.days_since == null ? "—" : `${p.days_since} d`}</p>
              <p className="text-xs text-slate-500">{p.last_grazed ? `${nzDate(p.last_grazed)}${p.mob ? ` · ${p.mob}` : ""}` : "Not grazed in the last year"}</p>
            </div>))}
        </div>)}
      <p className="text-xs text-slate-500 mt-3">From saved feed sheets: past days, plus today once it's confirmed (breaks and crop paddocks).</p>
    </Section>
  );
}

const Done = ({ d }) => (d
  ? <span className="inline-flex items-center gap-1 text-xs text-green-700" title={d.at}><CheckCircle size={14} weight="fill" />{d.by_name || "Done"}</span>
  : <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Circle size={14} />Not ticked</span>);

function HistoryDay({ d }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid={`history-${d.date}`}>
      <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
        <p className="font-semibold">{nzDate(d.date)}</p>
        <Pill tone={d.status === "confirmed" ? "green" : "slate"}>{d.status === "confirmed" ? "Confirmed" : "Planned only"}</Pill>
      </div>
      {d.notes && <p className="text-sm text-slate-600 mb-2">{d.notes}</p>}
      <table className="w-full text-sm table-fixed">
        <colgroup><col className="w-[24%]" /><col className="w-[16%]" /><col className="w-[40%]" /><col className="w-[20%]" /></colgroup>
        <tbody>{d.items.map((it, i) => [...it.breaks.map((b, j) => (
          <tr key={`${i}-b${j}`} className="border-t border-slate-100 align-top">
            <td className="py-1.5 pr-3 font-medium">{it.mob}{it.head ? <span className="text-xs text-slate-500"> · {num(it.head, 0)} hd</span> : null}</td>
            <td className="py-1.5 pr-3 text-slate-500">{b.label || "Break"}</td>
            <td className="py-1.5 pr-3 font-semibold">{b.paddock}{b.area_used ? <span className="font-normal text-slate-500"> · {num(b.area_used, 2)} ha</span> : null}{b.races_note && <span className="block text-xs font-normal text-slate-500">Races → {b.races_note}</span>}</td>
            <td className="py-1.5"><Done d={b.done} /></td>
          </tr>)), ...it.crops.map((c, j) => (
          <tr key={`${i}-c${j}`} className="border-t border-slate-100 align-top">
            <td className="py-1.5 pr-3 font-medium">{it.mob}</td>
            <td className="py-1.5 pr-3 text-slate-500">Crop</td>
            <td className="py-1.5 pr-3 font-semibold">{c.name}{c.paddock ? ` · ${c.paddock}` : ""}{c.area_ha != null && <span className="font-normal text-slate-500"> · {num(c.area_ha, 2)} ha</span>}</td>
            <td className="py-1.5"><Done d={c.done} /></td>
          </tr>))])}</tbody>
      </table>
      {d.wagon.length > 0 && <div className="mt-2 text-xs space-y-0.5">{d.wagon.map((w) => <p key={w.feed} className="flex flex-wrap gap-2">Wagon {w.feed}: {num(w.as_fed_kg, 0)} kg{w.loads ? ` · ${w.loads} load${w.loads > 1 ? "s" : ""}` : ""} <Done d={w.done} /></p>)}</div>}
      {d.minerals.length > 0 && <p className="mt-1 text-xs text-slate-600">Minerals: {d.minerals.map((m) => `${m.name} ${m.total != null ? `${num(m.total, 2)} ${m.total_unit}` : ""}`).join(" · ")}</p>}
    </div>
  );
}

export function GrazingHistory({ farm, tick }) {
  const api = useApi();
  const [f, setF] = useState({ start: minusDays(todayNZ(), 30), end: todayNZ(), paddock: "", mob: "" });
  const qs = `farm_id=${farm.id}&start=${f.start}&end=${f.end}&paddock=${encodeURIComponent(f.paddock)}&mob=${encodeURIComponent(f.mob)}`;
  const [days, , err] = useLoad(() => api.get(`/feeding/history?${qs}`), [qs, tick]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Section title="Grazing history" testid="grazing-history"
      actions={<Btn variant="outline" onClick={() => downloadFile(api, `/feeding/history/pdf?${qs}`, `grazing-history-${f.start}-to-${f.end}.pdf`)} data-testid="history-pdf"><FilePdf size={16} />Export PDF</Btn>}>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Field label="From"><input type="date" className="field-input !w-44" value={f.start} max={f.end} onChange={set("start")} data-testid="history-start" /></Field>
        <Field label="To"><input type="date" className="field-input !w-44" value={f.end} min={f.start} onChange={set("end")} data-testid="history-end" /></Field>
        <Field label="Paddock"><select className="field-input !w-44" value={f.paddock} onChange={set("paddock")} data-testid="history-paddock"><option value="">All paddocks</option>{farm.paddocks.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}</select></Field>
        <Field label="Mob"><select className="field-input !w-44" value={f.mob} onChange={set("mob")} data-testid="history-mob"><option value="">All mobs</option>{farm.mobs.map((m) => <option key={m} value={m}>{m}</option>)}</select></Field>
      </div>
      {err ? <p className="text-sm text-red-700" data-testid="history-error">{err}</p> : !days ? <p className="text-sm text-slate-500">Loading…</p>
        : days.length === 0 ? <Empty>No feed sheets in this range.</Empty>
        : <div className="space-y-3" data-testid="history-list">{days.map((d) => <HistoryDay key={d.date} d={d} />)}</div>}
    </Section>
  );
}
